import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Mail, Building2, FileDown, ChevronDown, Save, MailQuestion } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "negotiating", "client", "closed"];
const STATUS_COLORS = {
  new: "border-purple-500/40 text-purple-300 bg-purple-600/10",
  contacted: "border-sky-500/40 text-sky-300 bg-sky-600/10",
  negotiating: "border-[#E5C56F]/40 text-[#E5C56F] bg-[#E5C56F]/10",
  client: "border-emerald-500/40 text-emerald-300 bg-emerald-600/10",
  closed: "border-zinc-500/40 text-zinc-400 bg-zinc-600/10",
};

const ClientCard = ({ c, adminKey, onChange }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(c.notes || "");
  const [saving, setSaving] = useState(false);

  const patch = async (body) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/crm/clients/${encodeURIComponent(c.email)}`, body, {
      });
      onChange({ ...c, ...body });
      toast.success("Saved");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="rounded-2xl border border-white/10 bg-white/[0.02] hover:border-purple-500/30 transition-colors" data-testid={`crm-client-${c.email}`}>
      <button className="w-full text-left p-5 flex flex-wrap items-center gap-x-5 gap-y-2" onClick={() => setOpen(!open)} data-testid={`crm-client-toggle-${c.email}`}>
        <div className="min-w-[180px]">
          <div className="font-display text-lg text-zinc-100">{c.name || c.email}</div>
          <div className="text-xs text-zinc-500 flex items-center gap-1.5 mt-0.5"><Mail size={11} /> {c.email}</div>
        </div>
        {c.company && (
          <span className="text-xs text-zinc-400 inline-flex items-center gap-1.5"><Building2 size={11} /> {c.company}</span>
        )}
        <span className={`text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${STATUS_COLORS[c.status] || STATUS_COLORS.new}`}>
          {c.status}
        </span>
        <span className="text-xs text-zinc-500">{c.inquiries.length} inquir{c.inquiries.length === 1 ? "y" : "ies"}</span>
        {c.lead && (
          <span className="text-xs text-[#E5C56F] inline-flex items-center gap-1"><FileDown size={11} /> checklist ×{c.lead.downloads}</span>
        )}
        <span className="ml-auto text-xs text-zinc-600">
          {c.last_activity ? new Date(c.last_activity).toLocaleDateString() : ""}
        </span>
        <ChevronDown size={14} className={`text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-white/[0.06] pt-4 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mr-1">Status:</span>
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => patch({ status: s })}
                data-testid={`crm-status-${s}-${c.email}`}
                className={`text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border transition-colors ${
                  c.status === s ? STATUS_COLORS[s] : "border-white/10 text-zinc-500 hover:text-zinc-300"
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">Timeline</div>
            <div className="space-y-2">
              {c.lead && (
                <div className="text-xs text-zinc-400 flex gap-2">
                  <span className="text-[#E5C56F] shrink-0">⬇ Checklist</span>
                  downloaded ×{c.lead.downloads} · {c.lead.source || ""} · {c.lead.created_at ? new Date(c.lead.created_at).toLocaleString() : ""}
                </div>
              )}
              {c.inquiries.map((i, idx) => (
                <div key={`${i.created_at || i.service}-${idx}`} className="text-xs text-zinc-300 border-l-2 border-purple-500/40 pl-3 py-1">
                  <div className="text-purple-300 uppercase tracking-[0.15em] text-[10px]">{i.service}</div>
                  <div className="mt-1 whitespace-pre-wrap text-zinc-400">{i.message}</div>
                  <div className="text-zinc-600 mt-1">{i.created_at ? new Date(i.created_at).toLocaleString() : ""}</div>
                </div>
              ))}
              {c.inquiries.length === 0 && !c.lead && <div className="text-xs text-zinc-600">No activity yet.</div>}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">Notes</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Private notes about this client…"
              data-testid={`crm-notes-${c.email}`}
              className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/50"
            />
            <button
              onClick={() => patch({ notes })}
              disabled={saving}
              data-testid={`crm-save-notes-${c.email}`}
              className="mt-2 inline-flex items-center gap-2 rounded-lg bg-purple-700 hover:bg-purple-600 px-4 py-2 text-[10px] uppercase tracking-[0.3em] disabled:opacity-60"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save notes
            </button>
          </div>
        </div>
      )}
    </article>
  );
};

export const CrmPanel = ({ adminKey }) => {
  const [clients, setClients] = useState(null);
  const [gmail, setGmail] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/crm/clients`)
      .then(({ data }) => alive && setClients(data.clients || []))
      .catch(() => alive && setErr("Failed to load clients."));
    axios.get(`${API}/crm/gmail/status`)
      .then(({ data }) => alive && setGmail(data))
      .catch(() => alive && setGmail({ configured: false, connected: false }));
    return () => { alive = false; };
  }, [adminKey]);

  if (err) return <div className="text-red-400 text-sm">{err}</div>;
  if (clients === null)
    return <div className="flex items-center gap-3 text-zinc-400"><Loader2 className="animate-spin" size={16} /> Loading…</div>;

  return (
    <div className="space-y-5" data-testid="crm-panel">
      {gmail && !gmail.connected && (
        <div className="rounded-2xl border border-[#E5C56F]/25 bg-[#E5C56F]/[0.04] p-5 flex items-start gap-3" data-testid="crm-gmail-banner">
          <MailQuestion size={16} className="text-[#E5C56F] mt-0.5 shrink-0" />
          <div className="text-xs text-zinc-300 leading-relaxed">
            <span className="text-[#E5C56F] font-medium">Gmail not connected.</span>{" "}
            To see the email history with each client here, the corporate Gmail needs Google API credentials
            (GOOGLE_CLIENT_ID + GOOGLE_CLIENT_SECRET from Google Cloud Console). Ask your builder in chat to set it up.
          </div>
        </div>
      )}

      {clients.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center text-zinc-500">
          No clients yet — inquiries and checklist leads will appear here automatically.
        </div>
      )}

      {clients.map((c) => (
        <ClientCard
          key={c.email}
          c={c}
          adminKey={adminKey}
          onChange={(nc) => setClients((prev) => prev.map((x) => (x.email === nc.email ? nc : x)))}
        />
      ))}
    </div>
  );
};

export default CrmPanel;
